import { useCallback, useMemo, useReducer, useState } from 'react';
import { getTextLocalizer } from '../../../locale/useTextLocalizer';
import { useDirectSpeech } from '../../DirectSpeech';
let localize;
getTextLocalizer('NovaComponents-Tab-gui-icu').then(localizer => {
  localize = localizer;
});
export function getDuplicateTabNameValidator(_errorMessage) {
  return (editedItem, value, items) => {
    let errorStatus = null;
    let errorMessage = undefined;
    const duplicateLabels = items.filter(tabItem => tabItem !== editedItem && tabItem.label?.trim().toUpperCase() === value.trim().toUpperCase());
    if (duplicateLabels.length > 0) {
      errorMessage = _errorMessage ?? localize('tab.labelEdit.duplicateLabel.errorMessage.label', {
        label: value
      });
      errorStatus = 'alert';
    }
    return {
      status: errorStatus,
      message: errorMessage
    };
  };
}

// This hook deals with TabEditing, TabMenu, New Tab features.
export const useTabItemsState = input => {
  const narration = useDirectSpeech();
  const [selectedIndex, setSelectedIndex] = useState(() => input.initialSelectedIndex || 0);
  const labelValidator = input.labelValidator ?? getDuplicateTabNameValidator();
  const addAriaControlsAttribute = useCallback((items, selectedIndex) => {
    const updatedItems = items.map((item, index) => ({
      ...item,
      committedLabel: item.label,
      ariaControls: index === selectedIndex ? input.tabPanelId : undefined
    }));
    return updatedItems;
  }, [input.tabPanelId]);
  const [items, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'Edit':
        let clone, status, message;
        const {
          reason,
          itemIndex,
          item,
          newLabel
        } = action.payload;
        switch (reason) {
          case 'Change':
            clone = state.slice(0);
            ({
              status,
              message
            } = labelValidator(item, newLabel, state));
            clone[itemIndex] = {
              ...clone[itemIndex],
              message,
              status
            };
            return clone;
          case 'Commit':
            clone = state.slice(0);
            ({
              status,
              message
            } = labelValidator?.(item, newLabel, state));
            const shouldRevert = newLabel.trim() === '' || status || message;
            const newValue = shouldRevert ? state[itemIndex].committedLabel : newLabel;
            clone[itemIndex] = {
              ...clone[itemIndex],
              label: newValue,
              committedLabel: newValue,
              message: undefined,
              status: undefined
            };
            return clone;
          case 'Cancel':
            clone = state.slice(0);
            clone[itemIndex] = {
              ...clone[itemIndex],
              label: state[itemIndex].committedLabel,
              message: undefined,
              status: undefined
            };
            return clone;
          default:
            return state;
        }
        break;
      case 'Update':
        return action.payload.items.map(item => ({
          ...item,
          committedLabel: item.label
        }));
      case 'Add':
        const tabItems = state.slice(0);
        tabItems.splice(action.payload.index, 0, {
          ...action.payload.item,
          committedLabel: action.payload.item.label
        });
        return tabItems;
      case 'Reorder':
        const tabs = state.slice(0);
        const [removedTab] = tabs.splice(action.payload.oldIndex, 1);
        tabs.splice(action.payload.newIndex, 0, removedTab);
        return tabs;
      case 'Close':
        const tabsArr = state.slice(0);
        const tab = tabsArr[action.payload.itemIndex];
        const notifyMessage = tab.label ? localize('tab.label.direct.speech.close.msg', {
          label: tab.label
        }) : localize('tab.index.direct.speech.close.msg', {
          index: action.payload.itemIndex.toString()
        });
        narration?.notify(notifyMessage);
        tabsArr.splice(action.payload.itemIndex, 1);
        return tabsArr;
      case 'CloseAll':
        narration?.notify(localize('tab.direct.speech.closeAllTabs.msg'));
        return [];
      default:
        return state;
    }
  }, addAriaControlsAttribute(input.items, selectedIndex));
  function updateTabLabel(itemIndex, item, newLabel, type) {
    dispatch({
      type: 'Edit',
      payload: {
        reason: type,
        itemIndex,
        item,
        newLabel
      }
    });
  }
  const updateTabItems = useCallback(items => {
    dispatch({
      type: 'Update',
      payload: {
        items
      }
    });
  }, []);

  // can be enhanced to handle stateful menus.
  function onTabMenuItemSelect(itemIndex, item, location) {
    input.onTabMenuItemSelect?.(itemIndex, item, location);
  }
  function onItemSelected(e) {
    input.onItemSelected?.(e.item);
    setSelectedIndex(e.index);
  }
  function addNewTab(item, index) {
    let newItem = item;
    if (!newItem) {
      newItem = input.addNewTab?.(items);
    }
    /* -------------------------------------------
     *  Should not accept blank values
     * ---------------------------------------------*/
    if (newItem && newItem.label?.trim() !== '') {
      const newIndex = index ?? items.length;
      newItem && dispatch({
        type: 'Add',
        payload: {
          item: newItem,
          index: newIndex
        }
      });
      setSelectedIndex(newIndex);
    }
  }
  function reorderTabs(oldIndex, newIndex) {
    input.onReorder?.(oldIndex, newIndex);
    dispatch({
      type: 'Reorder',
      payload: {
        oldIndex,
        newIndex
      }
    });
    setSelectedIndex(newIndex);
  }
  const closeTab = itemIndex => {
    if (itemIndex === selectedIndex && itemIndex === items.length - 1) {
      const newSelectedIndex = items.length - 2 >= 0 ? items.length - 2 : 0;
      setSelectedIndex(newSelectedIndex);
    }
    dispatch({
      type: 'Close',
      payload: {
        itemIndex
      }
    });
  };
  const closeAllTab = () => {
    items.splice(0, items.length);
    dispatch({
      type: 'CloseAll'
    });
  };
  const itemsWithAriaControls = useMemo(() => {
    return addAriaControlsAttribute(items, selectedIndex);
  }, [items, selectedIndex, addAriaControlsAttribute]);
  return {
    selectedIndex,
    setSelectedIndex,
    onItemSelected,
    items: itemsWithAriaControls,
    updateTabItems,
    updateTabLabel,
    onTabMenuItemSelect,
    onTabMenuOpen: input.onTabMenuOpen,
    addNewTab,
    reorderTabs,
    closeTab,
    closeAllTab
  };
};
//# sourceMappingURL=useTabItemsState.js.map